/* Badkamervoorjou — independent sticky-cart controller. No second cart request.
 * The original product form remains the sole source of variant, quantity and availability.
 * The bar is portalled outside animated sections, without moving the product form.
 */
(() => {
  'use strict';
  if (customElements.get('bvj-sticky-cart')) return;
  const PREVIEW = '#preview-bar-iframe,#ShopifyPreviewBar,#PBarNextFrame,#PBarNextFrameWrapper,#shopify-preview-bar,[data-shopify-preview-bar]';

  class BvjStickyCart extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.bar = this.querySelector('.bvj-cart-bar');
      if (!this.bar) {
        this.frame = requestAnimationFrame(() => { this.frame = 0; if (this.isConnected) this.connectedCallback(); });
        return;
      }
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.button = this.bar.querySelector('[data-bvj-submit]');
      this.error = this.bar.querySelector('[data-bvj-error]');
      this.edit = this.bar.querySelector('[data-bvj-edit]');
      this.originalPadding = getComputedStyle(document.body).paddingBottom;
      document.body.appendChild(this.bar);
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.sync(); });
      };
      this.button.addEventListener('click', () => this.submit(), { signal });
      this.edit?.addEventListener('click', () => this.editSelection(), { signal });
      ['change', 'input', 'focusin', 'focusout'].forEach(name => {
        document.addEventListener(name, (event) => {
          if (name === 'input' || name === 'change') this.validationError = '';
          this.schedule();
        }, { signal });
      });
      ['resize', 'scroll', 'pageshow', 'orientationchange'].forEach(name => window.addEventListener(name, this.schedule, { signal, passive: true }));
      ['resize', 'scroll'].forEach(name => window.visualViewport?.addEventListener(name, this.schedule, { signal, passive: true }));
      document.addEventListener('visibilitychange', this.schedule, { signal });
      this.observer = new MutationObserver(records => {
        if (records.some(record => !this.bar.contains(record.target) && !(record.target === document.documentElement && record.attributeName === 'style'))) this.schedule();
      });
      this.observer.observe(document.body, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'aria-hidden', 'aria-expanded', 'class', 'hidden', 'open', 'value', 'style']
      });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.schedule);
        this.resizeObserver.observe(this.bar);
      }
      customElements.whenDefined('product-form').then(() => { if (this.isConnected) this.schedule(); });
      this.sync();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.lockTimer);
      this.frame = 0;
      if (this.bar) { this.bar.hidden = true; this.appendChild(this.bar); }
      const root = document.documentElement;
      root.classList.remove('bvj-cart-present');
      ['--bvj-cart-space', '--bvj-cart-original-padding'].forEach(name => root.style.removeProperty(name));
    }

    text(selector, value) {
      const node = this.bar.querySelector(selector);
      if (node && node.textContent !== value) node.textContent = value;
    }

    visible(node, intersect = true) {
      if (!node || !node.isConnected || !node.getClientRects().length) return false;
      for (let current = node; current && current !== document.documentElement; current = current.parentElement) {
        if (current.hidden || current.getAttribute('aria-hidden') === 'true' || current.hasAttribute('inert')) return false;
        const style = getComputedStyle(current);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
        if (current.matches('product-modal,quick-add-modal,modal-dialog,dialog') && !current.hasAttribute('open')) return false;
      }
      if (!intersect) return true;
      const rect = node.getBoundingClientRect();
      const view = window.visualViewport;
      return rect.width > 0 && rect.height > 0 && rect.bottom > (view?.offsetTop || 0) && rect.top < (view ? view.offsetTop + view.height : innerHeight) && rect.right > 0 && rect.left < innerWidth;
    }

    previewOffset() {
      const view = window.visualViewport;
      const bottom = view ? view.offsetTop + view.height : innerHeight;
      let offset = Math.max(0, innerHeight - bottom);
      document.querySelectorAll(PREVIEW).forEach(node => {
        if (!this.visible(node)) return;
        const rect = node.getBoundingClientRect();
        if (rect.width < innerWidth * 0.5 || rect.height > innerHeight * 0.5) return;
        if (rect.bottom >= bottom - 4 || rect.bottom >= innerHeight - 4) offset = Math.max(offset, innerHeight - rect.top);
      });
      return Math.ceil(Math.max(0, offset));
    }

    sync() {
      if (!this.isConnected || !this.bar || !this.button) return;
      this.main = document.querySelector('product-info[id^="MainProduct"]');
      const sameProduct = !!this.main && this.main.dataset.productId === this.dataset.productId;
      const component = sameProduct ? this.main.querySelector('product-form') : null;
      this.form = component?.querySelector('form');
      this.native = this.form?.querySelector('button[name="add"][type="submit"],button[id^="ProductSubmitButton"]');
      const input = this.form?.querySelector('input[name="id"]');
      const json = this.main?.querySelector('variant-selects [data-selected-variant]');
      let variant;
      try { variant = json ? JSON.parse(json.textContent) : null; } catch (_) { variant = null; }
      const mismatch = !!json && String(variant?.id || '') !== String(input?.value || '');
      const loading = !!this.native?.classList.contains('loading');
      const blocked = !sameProduct || !this.native || !input?.value || input.disabled || mismatch || this.native.disabled || this.native.getAttribute('aria-disabled') === 'true' || loading || !!this.clickLock;
      if (this.button.disabled !== blocked) this.button.disabled = blocked;
      const nativeLabel = this.native?.querySelector('span')?.textContent.trim();
      const label = loading || this.clickLock ? this.dataset.busyLabel : mismatch ? 'Controleer je keuze' : blocked ? nativeLabel || this.dataset.busyLabel : this.dataset.label;
      this.text('[data-bvj-submit]', label);
      this.button.setAttribute('aria-busy', loading || this.clickLock ? 'true' : 'false');
      if (this.form?.id) this.button.setAttribute('aria-controls', this.form.id);
      if (this.native?.hasAttribute('aria-haspopup')) this.button.setAttribute('aria-haspopup', this.native.getAttribute('aria-haspopup'));
      else this.button.removeAttribute('aria-haspopup');
      const priceRoot = this.main?.querySelector('[id^="price-"]');
      const price = priceRoot?.querySelector('.price--on-sale .price-item--sale') || priceRoot?.querySelector('.price__regular .price-item--regular') || priceRoot?.querySelector('.price-item');
      this.text('[data-bvj-price]', !input?.value || mismatch || priceRoot?.classList.contains('hidden') ? '—' : price?.textContent.trim().replace(/\s+/g, ' ') || '—');
      const installation = this.form?.querySelector('bvj-inbouw-choice')?.getSummary?.() || this.main?.querySelector('lagoo-configurator')?.getSummary?.();
      const unitSelector = '.bvj-cart-bar__price small > span:first-child';
      this.originalUnitLabel ??= this.bar.querySelector(unitSelector)?.textContent || 'per stuk';
      if (installation?.add && !mismatch && input?.value) {
        this.text('[data-bvj-price]', installation.totalPrice === null ? '—' : new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', { style: 'currency', currency: installation.currency || 'EUR' }).format(installation.totalPrice / 100));
      }
      this.text(unitSelector, installation?.add ? (installation.kind === 'sunshower' ? 'totaal met installatiebox' : 'totaal met inbouwdeel') : this.originalUnitLabel);
      this.quantity = this.form?.elements.namedItem('quantity') || this.main?.querySelector('.quantity__input');
      this.text('[data-bvj-quantity]', `${this.dataset.quantityLabel}: ${this.quantity?.value || '1'}`);
      this.text('[data-bvj-title]', this.main?.querySelector('.product__title h1')?.textContent.trim() || '');
      const selection = variant?.public_title || (variant?.title === 'Default Title' ? '' : variant?.title) || '';
      const sku = variant?.sku || this.main?.querySelector('[id^="Sku-"] strong')?.textContent.trim() || '';
      const installationLabel = installation ? (installation.add ? `Met ${installation.part.sku}` : installation.choice ? (installation.kind === 'sunshower' ? 'Zonder installatiebox' : 'Zonder inbouwdeel') : (installation.kind === 'sunshower' ? 'Kies je installatiebox' : 'Kies je inbouwdeel')) : '';
      this.text('[data-bvj-selection]', [sku, selection, installationLabel].filter(Boolean).join(' · '));
      const nativeError = component?.querySelector('.product-form__error-message-wrapper:not([hidden]) .product-form__error-message')?.textContent.trim();
      const installationError = this.form?.querySelector('[data-bvj-inbouw-error]:not([hidden])')?.textContent.trim();
      const message = nativeError || installationError || this.validationError || '';
      this.text('[data-bvj-error]', message);
      this.error.hidden = !message;
      const overlay = [...document.querySelectorAll('cart-drawer.active,.cart-notification.active,product-modal[open],quick-add-modal[open],modal-dialog[open],dialog[open],details.menu-opening[open],[role="dialog"][aria-modal="true"],#shopify-pc__banner')].some(node => !node.closest(PREVIEW) && this.visible(node));
      const view = window.visualViewport;
      const typing = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]),textarea,select');
      const keyboard = !!(view && typing && innerHeight - view.height > 150);
      const hidden = !sameProduct || !this.native || overlay || keyboard;
      if (this.bar.hidden !== hidden) this.bar.hidden = hidden;
      const offset = this.previewOffset();
      const offsetValue = `${offset}px`;
      if (this.bar.style.getPropertyValue('--bvj-cart-offset') !== offsetValue) this.bar.style.setProperty('--bvj-cart-offset', offsetValue);
      const root = document.documentElement;
      root.classList.toggle('bvj-cart-present', !hidden);
      root.style.setProperty('--bvj-cart-original-padding', this.originalPadding);
      const space = `${hidden ? 0 : Math.ceil(this.bar.getBoundingClientRect().height + offset)}px`;
      if (root.style.getPropertyValue('--bvj-cart-space') !== space) root.style.setProperty('--bvj-cart-space', space);
    }

    editSelection() {
      const installation = this.form?.querySelector('bvj-inbouw-choice') || this.main?.querySelector('lagoo-configurator');
      if (installation?.getSummary?.() && !installation.getSummary().choice) { installation.focusChoice(); return; }
      const target = this.main?.querySelector('variant-selects button,variant-selects select,variant-selects input:not([type="hidden"]),lagoo-configurator [data-lagoo-choice][aria-checked=true],.quantity__input');
      target?.scrollIntoView({ block: 'center', behavior: 'auto' });
      target?.focus({ preventScroll: true });
    }

    submit() {
      this.validationError = '';
      this.sync();
      if (this.button.disabled || !this.form || !this.native) return;
      const invalid = [...this.form.elements].find(node => node.willValidate && !node.validity.valid);
      if (invalid) {
        this.validationError = this.dataset.validationLabel;
        this.sync();
        invalid.scrollIntoView({ block: 'center', behavior: 'auto' });
        invalid.focus({ preventScroll: true });
        invalid.reportValidity();
        return;
      }
      this.clickLock = true;
      this.button.disabled = true;
      try { this.form.requestSubmit(this.native); }
      catch (_) { this.validationError = 'Gebruik de bestelknop bij je product om verder te gaan.'; }
      finally { this.lockTimer = setTimeout(() => { this.clickLock = false; this.schedule(); }, 650); }
      this.schedule();
    }
  }
  customElements.define('bvj-sticky-cart', BvjStickyCart);
})();


